import axios from "axios";
import { normalizeProhorText } from "@/store/prohor/prohorMatch";

const ACTIVE_TAG_KEY = "prohor_course_active_tag";

const COURSE_TAGS = [
    {
        slug: "analiz",
        title: "Анализ",
        aliases: ["анализ", "анализа", "анализу"],
    },
    {
        slug: "izmenenie",
        title: "Изменение",
        aliases: ["изменение", "изменения", "изменению"],
    },
    {
        slug: "lica",
        title: "Лица",
        aliases: ["лица", "лицам", "эмоции на лицах", "распознавание лиц"],
    },
    {
        slug: "raspoznavanie",
        title: "Распознавание",
        aliases: ["распознавание", "распознавания", "распознаванию"],
    },
    {
        slug: "zadanie-v-polya",
        title: "Задание «в поля»",
        aliases: ["в поля", "задание в поля"],
    },
    {
        slug: "prodazhi-peregovory",
        title: "Рост эффективности продаж и переговоров",
        aliases: ["продаж", "переговоров", "продажи"],
    },
    {
        slug: "konflikty",
        title: "Снижение количества конфликтов в команде",
        aliases: ["конфликт", "конфликтов"],
    },
    {
        slug: "stress-vygoranie",
        title: "Снижение стресса и профилактика выгорания",
        aliases: ["стресс", "выгоран"],
    },
    {
        slug: "trenazher",
        title: "Тренажёр",
        aliases: ["тренажер", "тренажёр"],
    },
    {
        slug: "teoriya",
        title: "Теория",
        aliases: ["теория", "теории"],
    },
];

const DEVELOP_PATTERNS = [
    /хочу\s+развив/,
    /хочу\s+развить/,
    /развивать/,
    /развить\s+(анализ|распознаван|изменен|лиц)/,
    /нач(ать|ну)\s+(развит|курс)/,
    /подбер(и|ите)\s+курс/,
    /выдай\s+курс/,
    /назначь\s+курс/,
    /траектори(я|ю)\s+развит/,
    /направление\s+(эи|эмоц)/,
];

const ADVANCE_PATTERNS = [
    /^дальше$/,
    /^далее$/,
    /следующ(ий|ий\s+курс)/,
    /следующий\s+этап/,
    /я\s+прошел/,
    /я\s+прошёл/,
    /курс\s+пройден/,
    /прошел\s+курс/,
    /прошёл\s+курс/,
    /двигаемся/,
    /продолж(им|аем|ить)/,
    /дай\s+следующ/,
    /следующий\s+по\s+тегу/,
];

const PROGRESS_PATTERNS = [
    /где\s+я\s+(по\s+)?(курс|путь|направлен)/,
    /какой\s+(сейчас\s+)?курс/,
    /мой\s+прогресс/,
    /прогресс\s+по\s+(курс|направлен|тегу)/,
    /на\s+каком\s+я\s+курс/,
];

const setActiveTag = (slug) => {
    try {
        if (slug) localStorage.setItem(ACTIVE_TAG_KEY, slug);
    } catch (e) {
        // ignore
    }
};

const getActiveTag = () => {
    try {
        return localStorage.getItem(ACTIVE_TAG_KEY) || null;
    } catch (e) {
        return null;
    }
};

const findTagInText = (normalized) => {
    for (const tag of COURSE_TAGS) {
        if (normalized.includes(tag.slug)) return tag;
        if (tag.aliases.some((alias) => normalized.includes(alias))) {
            return tag;
        }
    }
    return null;
};

export const detectCoursePathMode = (question) => {
    const normalized = normalizeProhorText(question);
    if (!normalized) return null;

    if (ADVANCE_PATTERNS.some((p) => p.test(normalized))) {
        return { mode: "advance", tag: findTagInText(normalized) };
    }
    if (PROGRESS_PATTERNS.some((p) => p.test(normalized))) {
        return { mode: "progress", tag: findTagInText(normalized) };
    }
    if (DEVELOP_PATTERNS.some((p) => p.test(normalized))) {
        return { mode: "assign", tag: findTagInText(normalized) };
    }

    const tagOnly = findTagInText(normalized);
    if (
        tagOnly &&
        (/хочу/.test(normalized) ||
            /курс/.test(normalized) ||
            /развит/.test(normalized))
    ) {
        return { mode: "assign", tag: tagOnly };
    }

    return null;
};

export const isCoursePathQuestion = (question) =>
    !!detectCoursePathMode(question);

const courseActions = (course) => {
    if (!course?.open_url) return [];
    return [
        {
            label: "Открыть курс",
            url: course.open_url,
        },
    ];
};

const formatAssignReply = (data) => {
    if (!data) {
        return {
            text: "Не удалось назначить курс. Попробуйте позже.",
            actions: [],
        };
    }

    if (data.action === "completed_tag") {
        return {
            text:
                data.message ||
                `Направление «${data.title}» уже пройдено.`,
            actions: [],
            suggestions: [],
        };
    }

    const step = (data.index ?? 0) + 1;
    const total = data.total ?? "?";
    const moodleId = data.course?.moodle_id;

    const text =
        (data.action === "already_assigned"
            ? `Продолжаем направление «${data.title}».\n`
            : `Отлично! Начинаем направление «${data.title}».\n`) +
        `Курс ${step} из ${total}` +
        (moodleId ? ` (Moodle №${moodleId})` : "") +
        ".\nОткройте курс по кнопке ниже. Когда пройдёте — напишите «дальше».";

    return {
        text,
        actions: courseActions(data.course),
        suggestions: ["Дальше", "Где я по курсу?"],
    };
};

const formatAdvanceReply = (data) => {
    if (!data) {
        return {
            text: "Не удалось перейти к следующему курсу.",
            actions: [],
        };
    }

    if (data.action === "not_started") {
        return {
            text:
                data.message ||
                "Сначала выберите направление — например: «Хочу развивать Анализ».",
            suggestions: COURSE_TAGS.filter((t) =>
                ["analiz", "lica", "raspoznavanie", "izmenenie"].includes(
                    t.slug
                )
            ).map((t) => `Хочу развивать ${t.title}`),
        };
    }

    if (data.action === "blocked") {
        return {
            text:
                (data.message || "Сначала завершите текущий курс.") +
                "\nКогда закончите в Moodle — снова напишите «дальше».",
            actions: courseActions(data.course),
            suggestions: ["Дальше", "Где я по курсу?"],
        };
    }

    if (data.action === "completed_tag") {
        return {
            text:
                data.message ||
                `Направление «${data.title}» пройдено. Можете выбрать другое.`,
            suggestions: [
                "Хочу развивать Лица",
                "Хочу развивать Распознавание",
            ],
        };
    }

    const step = (data.index ?? 0) + 1;
    const total = data.total ?? "?";

    return {
        text:
            `Курс пройден. Следующий шаг по «${data.title}»: курс ${step} из ${total}` +
            (data.course?.moodle_id ? ` (Moodle №${data.course.moodle_id})` : "") +
            ".\nОткройте курс и напишите «дальше», когда будете готовы.",
        actions: courseActions(data.course),
        suggestions: ["Дальше", "Где я по курсу?"],
    };
};

const formatProgressReply = (data) => {
    if (!data || data.status === "not_started") {
        return {
            text:
                "Пока нет активного курса по этому направлению. " +
                "Напишите, например: «Хочу развивать Анализ».",
            suggestions: ["Хочу развивать Анализ"],
        };
    }

    if (data.status === "completed") {
        return {
            text: `Направление «${data.title}» полностью пройдено.`,
            suggestions: ["Хочу развивать Лица"],
        };
    }

    const step = (data.index ?? 0) + 1;
    return {
        text:
            `Сейчас по «${data.title}»: курс ${step} из ${data.total}` +
            (data.current?.moodle_id
                ? ` (Moodle №${data.current.moodle_id})`
                : "") +
            (data.current?.completed
                ? ".\nТекущий курс уже пройден в Moodle — напишите «дальше»."
                : ".\nПройдите курс и напишите «дальше»."),
        actions: courseActions(data.current),
        suggestions: ["Дальше"],
    };
};

const resolveTagSlug = async (detectedTag) => {
    if (detectedTag?.slug) {
        setActiveTag(detectedTag.slug);
        return detectedTag.slug;
    }

    const stored = getActiveTag();
    if (stored) return stored;

    try {
        const { data } = await axios.get("/ai/course/path", {
            notifyShow: false,
        });
        const active = (data?.paths || []).find(
            (p) => p.status === "in_progress"
        );
        if (active?.tag) {
            setActiveTag(active.tag);
            return active.tag;
        }
    } catch (e) {
        // ignore
    }

    return null;
};

const pickTagPrompt = () => ({
    text:
        "Какое направление хотите развивать? Выберите ниже или напишите, например: «Хочу развивать Анализ».",
    suggestions: COURSE_TAGS.filter((t) =>
        ["analiz", "izmenenie", "lica", "raspoznavanie"].includes(t.slug)
    ).map((t) => `Хочу развивать ${t.title}`),
    actions: [],
});

export const handleCoursePathQuestion = async (question) => {
    const detected = detectCoursePathMode(question);
    if (!detected) return null;

    try {
        if (detected.mode === "assign") {
            if (!detected.tag) {
                return pickTagPrompt();
            }

            const { data } = await axios.post(
                `/ai/course/path/${detected.tag.slug}/assign`,
                {},
                { notifyShow: false }
            );
            setActiveTag(detected.tag.slug);
            return formatAssignReply(data);
        }

        const tagSlug = await resolveTagSlug(detected.tag);
        if (!tagSlug) {
            return pickTagPrompt();
        }

        if (detected.mode === "progress") {
            const { data } = await axios.get(`/ai/course/path/${tagSlug}`, {
                notifyShow: false,
            });
            setActiveTag(tagSlug);
            return formatProgressReply(data);
        }

        if (detected.mode === "advance") {
            const { data } = await axios.post(
                `/ai/course/path/${tagSlug}/advance`,
                {},
                { notifyShow: false }
            );
            setActiveTag(tagSlug);
            return formatAdvanceReply(data);
        }
    } catch (error) {
        const message =
            error?.response?.data?.message ||
            "Не удалось связаться с курсами. Попробуйте чуть позже.";
        return { text: message, actions: [] };
    }

    return null;
};
